<template>
  <div class="create-brief d-flex flex-column align-center px-3 pb-10">
    <v-card
      class="create-brief__card my-6 pa-5 pa-md-8"
      max-width="960"
      width="100%"
      outlined
    >
      <div class="d-flex align-start mb-6">
        <v-avatar
          color="primary"
          size="48"
          class="mr-4 flex-shrink-0"
        >
          <v-icon color="white">edit_note</v-icon>
        </v-avatar>
        <div>
          <div class="d-flex flex-wrap align-center">
            <h1 class="text-h5 font-weight-bold mb-0 mr-3">
              {{ isAnalyze ? $t('createBrief.analyzeTitle') : $t('createBrief.title') }}
            </h1>
            <v-chip
              x-small
              color="primary"
              outlined
              class="text-none font-weight-bold"
            >
              {{ isAnalyze ? $t('createBrief.analyzeBadge') : $t('createBrief.badge') }}
            </v-chip>
          </div>
          <p class="text--secondary mt-2 mb-0 body-2">
            {{ isAnalyze ? $t('createBrief.analyzeSubtitle') : $t('createBrief.subtitle') }}
          </p>
        </div>
      </div>

      <v-form ref="form">
        <BriefStep
          v-if="isAnalyze"
          :number="1"
          :title="$t('createBrief.steps.url.title')"
          :help="$t('createBrief.steps.url.help')"
        >
          <v-text-field
            v-model="form.url"
            :label="$t('createBrief.urlLabel')"
            :hint="$t('keywords.analyze.urlInputHint')"
            outlined
            persistent-hint
            required
            :rules="[rules.requiredRule, rules.validUrlRule]"
          />
        </BriefStep>

        <BriefStep
          v-if="!hideBrief"
          :number="stepBrief"
          :title="$t('createBrief.steps.brief.title')"
          :help="isAnalyze ? $t('createBrief.steps.brief.helpAnalyze') : $t('createBrief.steps.brief.help')"
        >
          <v-textarea
            v-model="form.aiPrompt"
            :label="$t('createBrief.briefLabel')"
            :hint="$t('createBrief.briefExample')"
            outlined
            persistent-hint
            required
            rows="3"
            :rules="[rules.requiredRule, rules.maxWordsRule(200)]"
            auto-grow
          />
        </BriefStep>

        <BriefStep
          :number="stepKeyword"
          :title="$t('createBrief.steps.keyword.title')"
          :help="$t('createBrief.steps.keyword.help')"
        >
          <KeywordInput
            ref="keywordInput"
            embedded
            hide-confirm
            :loading="loading"
            :query-params="queryParams"
            @input="onKeywordFields"
          />
        </BriefStep>

        <BriefStep
          :number="stepTemplate"
          :title="$t('createBrief.steps.template.title')"
          :help="$t('createBrief.steps.template.help')"
        >
          <v-row dense>
            <v-col
              v-for="id in templateIds"
              :key="id"
              cols="12"
              sm="6"
              md="4"
            >
              <v-card
                outlined
                class="create-brief__template pa-4 fill-height pointer"
                :class="{ 'create-brief__template--active': selectedTemplate === id }"
                @click="pickTemplate(id)"
              >
                <div class="d-flex align-center mb-2">
                  <v-icon
                    small
                    class="mr-2"
                    :color="selectedTemplate === id ? 'primary' : undefined"
                  >
                    {{ templateIcon(id) }}
                  </v-icon>
                  <span
                    class="font-weight-bold"
                    :class="{ 'primary--text': selectedTemplate === id }"
                  >{{ $t('createBrief.templates.' + id + '.title') }}</span>
                </div>
                <div class="caption text--secondary">
                  {{ $t('createBrief.templates.' + id + '.blurb') }}
                </div>
              </v-card>
            </v-col>
          </v-row>
        </BriefStep>

        <BriefStep
          :number="stepStyle"
          :title="$t('createBrief.steps.style.title')"
          :help="$t('createBrief.steps.style.help')"
          optional
        >
          <v-row dense>
            <v-col
              cols="12"
              md="4"
            >
              <v-select
                v-model="form.tone"
                :items="toneItems"
                :label="$t('createBrief.toneLabel')"
                outlined
                hide-details
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-select
                v-model="form.audience"
                :items="audienceItems"
                :label="$t('createBrief.audienceLabel')"
                outlined
                hide-details
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-select
                v-model="form.depth"
                :items="depthItems"
                :label="$t('createBrief.depthLabel')"
                outlined
                hide-details
              />
            </v-col>
          </v-row>
          <div class="d-flex align-center mb-1 mt-6">
            <span class="caption text--secondary text-uppercase font-weight-medium">
              {{ $t('createBrief.secondaryLabel') }}
            </span>
            <v-spacer />
            <span class="caption text--secondary">
              {{ $t('createBrief.secondaryCount', [form.secondaryKeywords.length]) }}
            </span>
          </div>
          <div class="secondary-row">
            <v-text-field
              v-model="secondaryDraft"
              :label="$t('createBrief.secondaryPlaceholder')"
              outlined
              hide-details
              class="secondary-row__input"
              @keydown.enter.prevent="addSecondary"
            />
            <v-btn
              class="secondary-row__btn text-none"
              outlined
              color="primary"
              height="56"
              @click="addSecondary"
            >
              <v-icon left small>add</v-icon>
              {{ $t('createBrief.addKeyword') }}
            </v-btn>
          </div>
          <div class="d-flex flex-wrap mt-2">
            <v-chip
              v-for="(word, index) in form.secondaryKeywords"
              :key="word + index"
              class="mr-2 mb-2 text-none"
              close
              color="primary"
              outlined
              @click:close="removeSecondary(index)"
            >
              {{ word }}
            </v-chip>
          </div>
          <div class="caption text--secondary mt-1">
            {{ $t('createBrief.secondaryHint') }}
          </div>
        </BriefStep>
      </v-form>

      <div class="create-brief__footer d-flex flex-column flex-sm-row align-sm-center mt-6 pt-6">
        <div class="caption text--secondary mb-4 mb-sm-0 mr-sm-4 flex-grow-1">
          {{ isAnalyze ? $t('createBrief.nextStepHintAnalyze') : $t('createBrief.nextStepHint') }}
        </div>
        <div class="d-flex">
          <v-btn
            text
            class="text-none mr-2"
            @click="$router.push('/')"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            depressed
            color="accent"
            class="text-none"
            :loading="loading"
            @click="submitBrief"
          >
            {{ isAnalyze ? $t('createBrief.submitAnalyze') : $t('createBrief.submit') }}
            <v-icon right>arrow_forward</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script lang="ts">
    import Vue from 'vue';
    import {rules} from "@/utils/rules";
    import KeywordInput from "@/components/keywords/KeywordInput.vue";
    import BriefStep from "@/components/keywords/BriefStep.vue";
    import {ArticleMetadata} from "@/model/Article";
    import {
        AUDIENCE_IDS,
        DEPTH_IDS,
        defaultWritingBrief,
        GEO_TEMPLATE_ICONS,
        GEO_TEMPLATE_IDS,
        TONE_IDS,
        type ArticleAudienceId,
        type ArticleDepthId,
        type ArticleToneId,
        type GeoTemplateId,
        normalizeGeoTemplate,
    } from "@/config/WritingBrief";
    import {normalizeKeywordPhrase} from '@/service/keywordInflection';
    import {isRedundantNavigation, routeQueryHasValues} from '@/utils/routeQuery';

    export default Vue.extend({
        name: "KeywordForm",
        components: {KeywordInput, BriefStep},
        props: {
            loading: {
                type: Boolean,
                default: false
            },
            isAnalyze: {
                type: Boolean,
                default: false
            },
            presetAiPrompt: {
                type: String,
                default: ''
            },
            presetLocationCode: {
                type: String,
                default: ''
            },
            presetLanguageCode: {
                type: String,
                default: ''
            },
            hideBrief: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            form: {
                keyword: '',
                aiPrompt: '',
                url: '',
                language_code: 'cs' as (string | null),
                location_code: '2203' as (string | null),
                geoTemplate: defaultWritingBrief.geoTemplate as GeoTemplateId,
                geoTemplateUserPicked: false,
                tone: defaultWritingBrief.tone as ArticleToneId,
                audience: defaultWritingBrief.audience as ArticleAudienceId,
                depth: defaultWritingBrief.depth as ArticleDepthId,
                secondaryKeywords: [] as string[],
            },
            secondaryDraft: '',
            rules: rules,
            templateIds: GEO_TEMPLATE_IDS,
        }),
        computed: {
            queryParams: function (): Record<string, string> {
                const {keyword, aiPrompt, langloc} = this.$route.query;
                if (keyword || aiPrompt || langloc) {
                    const [language_code, location_code] = (langloc as string || '-').split('-');
                    return {
                        keyword: (keyword as string),
                        aiPrompt: (aiPrompt as string),
                        language_code,
                        location_code
                    };
                }
                if (this.presetLocationCode || this.presetLanguageCode) {
                    return {
                        keyword: '',
                        aiPrompt: '',
                        language_code: this.presetLanguageCode,
                        location_code: this.presetLocationCode,
                    };
                }
                return {};
            },
            stepBrief: function (): number {
                return this.isAnalyze ? 2 : 1;
            },
            stepKeyword: function (): number {
                return this.isAnalyze ? 3 : 2;
            },
            stepTemplate: function (): number {
                return this.isAnalyze ? 4 : 3;
            },
            stepStyle: function (): number {
                return this.isAnalyze ? 5 : 4;
            },
            selectedTemplate: function (): GeoTemplateId {
                return normalizeGeoTemplate(this.form.geoTemplate);
            },
            toneItems: function () {
                return TONE_IDS.map((id) => ({
                    value: id,
                    text: String(this.$t('createBrief.tones.' + id)),
                }));
            },
            audienceItems: function () {
                return AUDIENCE_IDS.map((id) => ({
                    value: id,
                    text: String(this.$t('createBrief.audiences.' + id)),
                }));
            },
            depthItems: function () {
                return DEPTH_IDS.map((id) => ({
                    value: id,
                    text: String(this.$t('createBrief.depths.' + id)),
                }));
            },
        },
        watch: {
            presetAiPrompt: function (value: string) {
                if (value) {
                    this.form.aiPrompt = value;
                }
            },
        },
        mounted: function () {
            if (this.presetAiPrompt) {
                this.form.aiPrompt = this.presetAiPrompt;
            } else if (this.queryParams.aiPrompt) {
                this.form.aiPrompt = this.queryParams.aiPrompt;
            }
            if (this.queryParams.keyword) {
                this.form.keyword = this.queryParams.keyword;
            }
            this.form.secondaryKeywords = (this.form.secondaryKeywords || [])
                .map((item) => String(item || '').replace(/\s+/g, ' ').trim())
                .filter((item) => item && normalizeKeywordPhrase(item) !== normalizeKeywordPhrase(this.form.keyword));
        },
        methods: {
            templateIcon: function (id: GeoTemplateId): string {
                return GEO_TEMPLATE_ICONS[id];
            },
            pickTemplate: function (id: GeoTemplateId): void {
                this.form.geoTemplate = id;
                this.form.geoTemplateUserPicked = true;
            },
            onKeywordFields: function (keywordInputForm: ArticleMetadata): void {
                this.form.keyword = keywordInputForm.keyword;
                this.form.location_code = keywordInputForm.location_code;
                this.form.language_code = keywordInputForm.language_code;
                this.form.secondaryKeywords = (this.form.secondaryKeywords || [])
                    .map((item) => String(item || '').replace(/\s+/g, ' ').trim())
                    .filter((item) => item && normalizeKeywordPhrase(item) !== normalizeKeywordPhrase(this.form.keyword));
            },
            addSecondary: function (): void {
                const word = this.secondaryDraft.trim();
                this.secondaryDraft = '';
                if (!word) {
                    return;
                }
                if (normalizeKeywordPhrase(word) === normalizeKeywordPhrase(this.form.keyword)) {
                    return;
                }
                if (!this.form.secondaryKeywords.includes(word)) {
                    this.form.secondaryKeywords.push(word);
                }
            },
            removeSecondary: function (index: number): void {
                this.form.secondaryKeywords.splice(index, 1);
            },
            clearRouteQuery: function (): void {
                const query = this.$route && this.$route.query ? this.$route.query : null;
                if (!routeQueryHasValues(query) || !this.$router) {
                    return;
                }
                try {
                    const pending = this.$router.replace({query: {}});
                    if (pending && typeof (pending as Promise<unknown>).catch === 'function') {
                        (pending as Promise<unknown>).catch((error: {name?: string; message?: string}) => {
                            if (isRedundantNavigation(error)) {
                                return undefined;
                            }
                            return Promise.reject(error);
                        });
                    }
                } catch (error) {
                    if (!isRedundantNavigation(error as {name?: string; message?: string})) {
                        throw error;
                    }
                }
            },
            submitBrief: function (): void {
                const formRef = this.$refs.form as { validate?: () => boolean };
                const keywordInput = this.$refs.keywordInput as { confirmKeywordForm?: () => boolean };
                const briefOk = !formRef || !formRef.validate || formRef.validate();
                const keywordOk = !keywordInput || !keywordInput.confirmKeywordForm || keywordInput.confirmKeywordForm();
                const promptRequired = this.rules.requiredRule(this.form.aiPrompt);
                const promptWords = this.rules.maxWordsRule(200)(this.form.aiPrompt);
                const hiddenPromptOk = !this.hideBrief || (promptRequired === true && promptWords === true);
                if (!briefOk || !keywordOk || !hiddenPromptOk) {
                    return;
                }
                this.form.secondaryKeywords = (this.form.secondaryKeywords || [])
                    .map((item) => String(item || '').replace(/\s+/g, ' ').trim())
                    .filter((item) => item && normalizeKeywordPhrase(item) !== normalizeKeywordPhrase(this.form.keyword));
                this.clearRouteQuery();
                this.$emit('input', {
                    keyword: this.form.keyword,
                    aiPrompt: this.form.aiPrompt,
                    url: this.form.url,
                    language_code: this.form.language_code,
                    location_code: this.form.location_code,
                    geoTemplate: this.selectedTemplate,
                    geoTemplateUserPicked: this.form.geoTemplateUserPicked,
                    tone: this.form.tone,
                    audience: this.form.audience,
                    depth: this.form.depth,
                    secondaryKeywords: this.form.secondaryKeywords.slice(),
                });
            },
        }
    });
</script>

<style scoped lang="sass">
.create-brief__card
    border-radius: 16px !important
    min-width: 0
    box-sizing: border-box

.create-brief__template
    border-radius: 12px !important
    border: 2px solid rgba(0, 0, 0, 0.12) !important
    transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease
    &--active
        border-color: var(--v-primary-base) !important
        background: rgba(8, 135, 220, 0.08) !important
        box-shadow: inset 0 0 0 1px var(--v-primary-base)

.create-brief__footer
    border-top: 1px solid rgba(0, 0, 0, 0.08)

.pointer
    cursor: pointer

$secondary-control-height: 56px

.secondary-row
    display: flex
    align-items: flex-start
    gap: 8px

.secondary-row__input
    flex: 1 1 auto
    margin: 0 !important

    ::v-deep .v-input__control > .v-input__slot
        min-height: $secondary-control-height !important
        height: $secondary-control-height !important
        margin-bottom: 0 !important

.secondary-row__btn.v-btn
    height: $secondary-control-height !important
    min-height: $secondary-control-height !important
    margin: 0 !important
    flex: 0 0 auto
    align-self: flex-start
    white-space: nowrap
</style>
